import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { CommitUploadSessionRequest } from '../../Model/requests/CommitUploadSessionRequest';
import ServicesContext from '../../global/services/ServicesContext';
import { useContext } from 'react';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { searchKeys } from '../search/queryKeyFactory';
import { useSearchContext } from '../search/SearchContext';
import { v4 as uuidv4 } from 'uuid';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';
import { FileHelper } from '../../helpers/FileHelper';

export function useCommitUploadSession(): UseMutationResult<string, unknown, { dto: CommitUploadSessionRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { updateCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const commitUploadSession = (data: { dto: CommitUploadSessionRequest }): Promise<string> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.commitUploadSession(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.commitUploadSession,
    mutationFn: (data: { dto: CommitUploadSessionRequest }) => commitUploadSession(data),
    onSuccess: async (result, variables) => {
      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }

      const fileName = variables.dto.fileName;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        type: 'success',
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentSuccessTitle', {
          fileName
        }),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentSuccessMessage'),
        progress: {
          current: 100,
          percent: 100,
          total: variables.dto.lastChunkIndex
        }
      });
    },
    onError: (data, variables) => {
      showToast({
        type: GEAR_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle')
      });

      const fileName = variables.dto.fileName;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedMessage', {
          fileName: variables.dto.fileName
        }),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
